import { getStays } from '@api/store';
import { TStore } from '@components/types';
import { getSafeUrl, sanitizeStaysRef } from '@core/utils';
import { Building2, Loader, MapPin, Search, X } from 'lucide-react';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';

const PAGE_SIZE = 200;
const MAX_PAGES = 10;
const MAX_RESULTS = 6;

type CityOption = { kind: 'city'; id: number; name: string; state?: string; count: number; href: string };
type PropertyOption = { kind: 'property'; id: number; name: string; place: string; href: string };
type Option = CityOption | PropertyOption;

// Hotel list is small (a few dozen); load it once per page view and share it between mounts.
let hotelsCache: Promise<TStore[]> | null = null;
const loadHotels = () => {
    if (!hotelsCache) {
        hotelsCache = (async () => {
            const all: TStore[] = [];
            for (let page = 0; page < MAX_PAGES; page++) {
                const batch = await getStays('', page, PAGE_SIZE);
                if (!batch?.length) break;
                all.push(...batch);
                if (batch.length < PAGE_SIZE) break;
            }
            if (!all.length) hotelsCache = null; // an empty list is almost certainly a failed load; retry next time
            return all;
        })().catch((e) => {
            hotelsCache = null; // allow a retry on the next focus
            throw e;
        });
    }
    return hotelsCache;
};

// Match at the start of any word ("rish" finds Rishikesh, not Krishan).
// Reads ?q= straight from the URL so it works on first load without waiting for router.isReady
// (which stays false for static pages opened with a query string), and follows later navigations.
export const useStaySearchTerm = () => {
    const router = useRouter();
    const [state, setState] = useState({ term: '', ready: false });
    useEffect(() => {
        const read = () => setState({ term: (new URLSearchParams(window.location.search).get('q') || '').trim(), ready: true });
        read();
        router.events.on('routeChangeComplete', read);
        return () => router.events.off('routeChangeComplete', read);
    }, [router.events]);
    return state;
};

const matches = (text: string | undefined, term: string) => {
    const t = (text || '').toLowerCase();
    return t.startsWith(term) || t.includes(` ${term}`) || t.includes(`(${term}`) || t.includes(`-${term}`);
};

interface StaySearchProps {
    className?: string;
    // Slim variant for the navbar: light bordered field, no Search button (Enter submits).
    compact?: boolean;
}

export const StaySearch: React.FC<StaySearchProps> = ({ className = '', compact = false }) => {
    const router = useRouter();
    const [term, setTerm] = useState('');
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const [hotels, setHotels] = useState<TStore[]>([]);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);
    const listId = 'stay-search-options';

    const ensureLoaded = () => {
        if (hotels.length || loading) return;
        setLoading(true);
        setFailed(false);
        loadHotels()
            .then(setHotels)
            .catch(() => setFailed(true))
            .finally(() => setLoading(false));
    };

    // Seed the box from ?q= so a shared search URL shows what it is filtering by.
    const { term: urlTerm } = useStaySearchTerm();
    useEffect(() => { setTerm(urlTerm); }, [urlTerm]);

    useEffect(() => {
        const onDown = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, []);

    const cities = useMemo<CityOption[]>(() => {
        const byId = new Map<number, CityOption>();
        hotels.forEach((h) => {
            const id = h.address?.city_id;
            const name = h.address?.city?.name;
            if (!id || !name) return;
            const existing = byId.get(id);
            if (existing) {
                existing.count += 1;
            } else {
                byId.set(id, {
                    kind: 'city', id, name, count: 1,
                    state: h.address?.state?.name,
                    href: `/stays/in/${getSafeUrl(name)}-hotels?city_id=${id}`,
                });
            }
        });
        return Array.from(byId.values()).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    }, [hotels]);

    // Where the stay page's "All Hotels" link should lead back to: this listing/search page.
    const refPath = sanitizeStaysRef(router.asPath) || '/stays';

    const options = useMemo<Option[]>(() => {
        const q = term.trim().toLowerCase();
        const cityHits = (q ? cities.filter((c) => matches(c.name, q) || matches(c.state, q)) : cities).slice(0, MAX_RESULTS);
        const propertyHits: PropertyOption[] = q
            ? hotels
                .filter((h) => matches(h.name, q) || matches(h.address?.locality, q))
                .slice(0, MAX_RESULTS)
                .map((h) => ({
                    kind: 'property' as const, id: h.id, name: h.name,
                    place: [h.address?.locality, h.address?.city?.name].filter(Boolean).join(', '),
                    href: `/stays/${h.id}/${getSafeUrl(h.name)}?ref=${encodeURIComponent(refPath)}`,
                }))
            : [];
        return [...cityHits, ...propertyHits];
    }, [term, cities, hotels, refPath]);

    const go = (href: string) => {
        setOpen(false);
        router.push(href);
    };

    const submit = () => {
        if (active >= 0 && options[active]) return go(options[active].href);
        const q = term.trim();
        go(q ? `/stays?q=${encodeURIComponent(q)}` : '/stays');
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActive((i) => (options.length ? (i + 1) % options.length : -1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => (options.length ? (i <= 0 ? options.length - 1 : i - 1) : -1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            submit();
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    const firstProperty = options.findIndex((o) => o.kind === 'property');

    return (
        <div ref={wrapRef} className={`relative w-full ${compact ? '' : 'max-w-2xl'} ${className}`}>
            <form
                role="search"
                onSubmit={(e) => { e.preventDefault(); submit(); }}
                className={compact
                    ? 'flex items-center gap-2 bg-neutral-50 border border-solid border-neutral-200 rounded-lg px-2 text-neutral-800 focus-within:border-neutral-400'
                    : 'flex items-center gap-2 bg-white rounded-2xl shadow-lg p-2 text-neutral-800'}
            >
                <Search className={`${compact ? 'w-4 h-4' : 'w-5 h-5 ml-2'} text-neutral-400 flex-shrink-0`} aria-hidden="true" />
                <input
                    type="text"
                    value={term}
                    onChange={(e) => { setTerm(e.target.value); setActive(-1); setOpen(true); ensureLoaded(); }}
                    onFocus={() => { ensureLoaded(); setOpen(true); }}
                    onKeyDown={onKeyDown}
                    placeholder={compact ? 'Search city or hotel' : 'Search a city or property name'}
                    aria-label="Search hotels by city or property name"
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={listId}
                    aria-autocomplete="list"
                    aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
                    autoComplete="off"
                    className={`flex-1 min-w-0 bg-transparent border-0 outline-none text-neutral-800 placeholder:text-neutral-400 ${compact ? 'h-9 text-sm' : 'h-10 text-base'}`}
                />
                {term && (
                    <button type="button" aria-label="Clear search" onClick={() => { setTerm(''); setActive(-1); setOpen(true); }}
                        className="p-1.5 rounded-full text-neutral-400 hover:bg-neutral-100 border-0 bg-transparent cursor-pointer">
                        <X className="w-4 h-4" />
                    </button>
                )}
                {!compact && <button type="submit"
                    className="h-10 px-5 rounded-xl text-white text-sm font-semibold border-0 cursor-pointer hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: '#ED7829' }}>
                    Search
                </button>}
            </form>

            {open && (
                <ul id={listId} role="listbox"
                    className={`absolute left-0 right-0 ${compact ? 'mt-1' : 'mt-2'} z-30 bg-white rounded-2xl shadow-xl border border-neutral-100 list-none p-2 m-0 max-h-80 overflow-auto text-left text-neutral-800`}>
                    {loading && (
                        <li className="flex items-center gap-2 px-3 py-3 text-sm text-neutral-500">
                            <Loader className="w-4 h-4 animate-spin" /> Loading destinations…
                        </li>
                    )}
                    {failed && (
                        <li className="px-3 py-3 text-sm text-neutral-500">
                            Couldn&rsquo;t load suggestions. Press Search to look up &ldquo;{term}&rdquo;.
                        </li>
                    )}
                    {!loading && !failed && options.length === 0 && (
                        <li className="px-3 py-3 text-sm text-neutral-500">No matching city or property.</li>
                    )}
                    {options.map((o, i) => (
                        <React.Fragment key={`${o.kind}-${o.id}`}>
                            {(i === 0 || i === firstProperty) && (
                                <li role="presentation" className="px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wider text-neutral-400">
                                    {o.kind === 'city' ? 'Cities' : 'Properties'}
                                </li>
                            )}
                            <li
                                id={`${listId}-${i}`}
                                role="option"
                                aria-selected={i === active}
                                onMouseEnter={() => setActive(i)}
                                onMouseDown={(e) => { e.preventDefault(); go(o.href); }}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer ${i === active ? 'bg-neutral-100' : ''}`}
                            >
                                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-neutral-100 text-neutral-500 flex-shrink-0">
                                    {o.kind === 'city' ? <MapPin className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block text-sm font-semibold truncate">{o.name}</span>
                                    <span className="block text-xs text-neutral-400 truncate">
                                        {o.kind === 'city'
                                            ? `${o.state ? `${o.state} · ` : ''}${o.count} propert${o.count === 1 ? 'y' : 'ies'}`
                                            : o.place}
                                    </span>
                                </span>
                            </li>
                        </React.Fragment>
                    ))}
                </ul>
            )}
        </div>
    );
};
